import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import qapi from "views_shared/app/javascript/api";
import MediaItem from "./MediaItem";
import MediaItemEditor from "./MediaItemEditor";
import ConfirmModal from "../confirm_modal";
import ServerErrorAlert from "../profiles/ServerErrorAlert";
import PaginationGroup from "../profiles/PaginationGroup";

const MEDIA_ITEMS_PER_PAGE = 5;

// Self-fetching media-items list for a PromoEntity owner, with an optional owner-editing surface
// layered on top of the original read-only widget. Hosts that supply their own heading (e.g. a profile
// section card) pass showTitle={false}; emptyMessage renders a neutral empty state instead of
// disappearing entirely. onEmptyChange lets a caller that wraps this in its own occupancy guard (e.g.
// ExpertSections' d-none wrapper, which starts hidden and only reveals once told the section isn't
// empty) learn the section's occupancy. Three cases:
//   1. A successful read: onEmptyChange(rows.length === 0) -- the read is the authority on occupancy.
//   2. A failed read (resolved `{ error }` OR rejected promise) with ZERO rows currently on screen:
//      onEmptyChange(false) -- NOT confirmed empty, so the wrapper must reveal or its retry banner
//      would render inert behind a hidden ancestor. A truly-empty profile may briefly show a section
//      with just a retry banner on a transient failure; that's correct -- we genuinely don't know yet.
//   3. A failed read with rows already on screen: onEmptyChange is NOT called -- occupancy is already
//      `false` from the earlier success, and calling it again would be redundant, not incorrect.
// The retry/error state itself (see loadError below) is what an editable OR non-editable viewer sees
// for the failure.
//
// `editable` + `ownerResource` turn on the add/edit/delete affordances: `ownerResource` is the nested
// route segment for the owner ("experts", "companies", "products" -- see Id::Engine routes'
// quotable_and_media_itemable concern). `ownerType` stays the PostgREST/read-side concept (the
// concrete STI owner_type string); the two are never conflated -- a read only ever needs the former,
// a mutation only ever needs the latter.
function MediaItems({
  ownerId,
  ownerType,
  ownerResource = undefined,
  editable = false,
  showTitle = true,
  emptyMessage = null,
  onEmptyChange = () => {},
}) {
  const [mediaItems, setMediaItems] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  // Reorder (up/down) and the featured-star toggle share one in-flight flag: both mutate list_position
  // and/or featured server-side, and the row array isn't updated optimistically, so a second click
  // against stale positions/flags before the refetch lands could target the wrong row. Blocking every
  // row's arrows/star for the duration of any one of these mutations -- not just the acting row's --
  // avoids that race; the edit/delete controls are unaffected (their own `disabled` already covers the
  // editingId case above).
  const [rowActionPending, setRowActionPending] = useState(false);
  const [rowActionError, setRowActionError] = useState(null);
  const [page, setPage] = useState(1);

  // `load` must read the CURRENT row count on a failure (case 2 vs. 3 above), but must not be recreated
  // every time `mediaItems` changes -- doing so would re-fire the effect below on every successful
  // load, an infinite fetch loop. A ref mirrors `mediaItems` on every render without going in `load`'s
  // own dependency array.
  const mediaItemsRef = useRef(mediaItems);
  mediaItemsRef.current = mediaItems;

  // The one awaitable loader for both the initial read AND post-mutation reconciliation. A failed load
  // (a resolved `{ error }` OR a rejected promise -- a network failure rejects rather than resolving an
  // error shape) never overwrites `mediaItems`: the last successfully loaded rows are retained. See the
  // three-case onEmptyChange contract in the file header comment. The failure is surfaced separately
  // via loadError, which the render below uses to show a retry affordance instead of silently rendering
  // nothing.
  const load = useCallback(async () => {
    let response;
    try {
      response = await qapi.mediaItems.getAllByOwner(ownerType, ownerId);
    } catch (e) {
      response = { error: e };
    }

    if (Array.isArray(response?.data)) {
      setMediaItems(response.data);
      setLoadError(null);
      onEmptyChange(response.data.length === 0);
      return response.data;
    }
    setLoadError(response?.error ?? new Error("Failed to load media items"));
    if (mediaItemsRef.current.length === 0) {
      onEmptyChange(false);
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerId, ownerType]);

  useEffect(() => {
    load();
  }, [load]);

  const showPageOf = (rows, mediaItemId) => {
    const index = rows ? rows.findIndex((row) => row.id === mediaItemId) : -1;
    if (index >= 0) {
      setPage(Math.floor(index / MEDIA_ITEMS_PER_PAGE) + 1);
    }
  };

  const createMediaItem = async (attributes) => {
    const existingIds = new Set(mediaItems.map((mediaItem) => mediaItem.id));
    await qapi.mediaItems.createForOwner(ownerResource, ownerId, attributes);
    const rows = await load();
    const created = rows?.find((row) => !existingIds.has(row.id));
    if (created) {
      showPageOf(rows, created.id);
    }
  };

  const updateMediaItem = async (id, attributes) => {
    await qapi.mediaItems.updateById(ownerResource, ownerId, id, attributes);
    await load();
  };

  const confirmDelete = async () => {
    if (deleting || pendingDeleteId == null) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await qapi.mediaItems.deleteById(ownerResource, ownerId, pendingDeleteId);
      setPendingDeleteId(null);
      await load();
    } catch (e) {
      setDeleteError(e);
    } finally {
      setDeleting(false);
    }
  };

  // Moves `mediaItem` to its immediate neighbor's list_position -- a server-side swap (acts_as_list
  // shifts the neighbor the other way), not a delta the client computes on its own. On failure the
  // rows are left exactly as loaded (no optimistic reorder to roll back) and the error surfaces via
  // rowActionError; on success the list reloads from the server so both rows land at their true,
  // server-computed positions.
  const moveMediaItem = async (mediaItem, direction) => {
    if (rowActionPending) return;
    const currentIndex = mediaItems.findIndex((m) => m.id === mediaItem.id);
    const targetIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
    if (currentIndex === -1 || targetIndex < 0 || targetIndex >= mediaItems.length) return;

    setRowActionPending(true);
    setRowActionError(null);
    try {
      await qapi.mediaItems.updateListPositionById(
        ownerResource,
        ownerId,
        mediaItem.id,
        mediaItems[targetIndex].list_position,
      );
      showPageOf(await load(), mediaItem.id);
    } catch (e) {
      setRowActionError(e);
    } finally {
      setRowActionPending(false);
    }
  };

  const toggleFeatured = async (mediaItem) => {
    if (rowActionPending) return;
    setRowActionPending(true);
    setRowActionError(null);
    try {
      await qapi.mediaItems.setFeaturedById(ownerResource, ownerId, mediaItem.id, !mediaItem.featured);
      showPageOf(await load(), mediaItem.id);
    } catch (e) {
      setRowActionError(e);
    } finally {
      setRowActionPending(false);
    }
  };

  // A non-editable viewer with nothing to show (and no load failure to report) renders the neutral
  // empty state (or nothing) exactly as before -- the editing surface below only ever applies to an
  // editable viewer. A load failure always falls through to the full render below instead: an initial
  // failure has zero local rows, but "zero rows because nothing has loaded yet" must not read the same
  // as "confirmed empty" -- the retry state has to be reachable for every viewer, not just an editor.
  if (!editable && mediaItems.length === 0 && !loadError) {
    return emptyMessage ? <div className="text-muted">{emptyMessage}</div> : null;
  }

  const pageCount = Math.max(1, Math.ceil(mediaItems.length / MEDIA_ITEMS_PER_PAGE));
  const currentPage = Math.min(page, pageCount);
  const pageStart = (currentPage - 1) * MEDIA_ITEMS_PER_PAGE;

  return (
    <>
      {showTitle && (
        <>
          <h3>Media Items</h3>
          <div className="mb-2" />
        </>
      )}

      {loadError && (
        <div className="alert alert-warning d-flex justify-content-between align-items-center">
          <span>
            {mediaItems.length > 0
              ? "Couldn't refresh media items — showing the last saved list."
              : "Couldn't load media items."}
          </span>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={load}>
            Retry
          </button>
        </div>
      )}

      {/* Suppressed while loadError is set: a failed read is not a confirmed empty list, so telling the
          viewer "no media added yet" alongside the retry banner above would be a contradiction. */}
      {mediaItems.length === 0 && !adding && !loadError && emptyMessage && (
        <div className="text-muted mb-2">{emptyMessage}</div>
      )}

      {editable && <ServerErrorAlert generalError={rowActionError} />}

      <div>
        {mediaItems.slice(pageStart, pageStart + MEDIA_ITEMS_PER_PAGE).map((mediaItem, pageIndex) => {
          const index = pageStart + pageIndex;
          return editable && editingId === mediaItem.id ? (
            <MediaItemEditor
              key={mediaItem.id}
              initialMediaItem={mediaItem}
              onSave={(attributes) => updateMediaItem(mediaItem.id, attributes)}
              onClose={() => setEditingId(null)}
            />
          ) : (
            <MediaItem
              key={mediaItem.id}
              mediaItem={mediaItem}
              editable={editable}
              onEdit={() => setEditingId(mediaItem.id)}
              onDelete={() => {
                setDeleteError(null);
                setPendingDeleteId(mediaItem.id);
              }}
              onMoveUp={() => moveMediaItem(mediaItem, "up")}
              onMoveDown={() => moveMediaItem(mediaItem, "down")}
              onToggleFeatured={() => toggleFeatured(mediaItem)}
              canMoveUp={index > 0}
              canMoveDown={index < mediaItems.length - 1}
              disabled={(editingId != null && editingId !== mediaItem.id) || rowActionPending}
            />
          );
        })}
      </div>

      <PaginationGroup
        className={editable ? "my-4" : "mt-4"}
        page={currentPage}
        pageCount={pageCount}
        onPageChange={setPage}
        ariaLabel="Media pages"
        disabled={editingId != null}
      />

      {editable &&
        (adding ? (
          <MediaItemEditor onSave={createMediaItem} onClose={() => setAdding(false)} />
        ) : (
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => setAdding(true)}
            disabled={editingId != null}
          >
            Add a media item
          </button>
        ))}

      {pendingDeleteId != null && (
        <ConfirmModal
          confirmYesText="Remove"
          confirmNoText="Cancel"
          confirmContent={
            <>
              <div>Remove this media item?</div>
              <ServerErrorAlert generalError={deleteError} />
            </>
          }
          confirmYes={confirmDelete}
          confirmNo={deleting ? undefined : () => setPendingDeleteId(null)}
          isOpening
          className="modal-sm p-3"
          confirmNoButtonClassName="shadow-none"
          isSubmitting={deleting}
        />
      )}
    </>
  );
}

MediaItems.propTypes = {
  ownerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  ownerType: PropTypes.string.isRequired,
  ownerResource: PropTypes.string,
  editable: PropTypes.bool,
  showTitle: PropTypes.bool,
  emptyMessage: PropTypes.string,
  onEmptyChange: PropTypes.func,
};

export default MediaItems;
